/**
 * Encounter System — Tuxemon-inspired wild Surggie encounters.
 * Manages encounter state machine: search → encounter → battle → catch/defect.
 */

import type { Position } from "./constants";
import { SPECIES_DEX } from "@/data/species";
import type { Zone } from "@/data/zones";

// Room → species id pool mapping.
// Pools draw from the full 30-species dex (see data/species.ts), grouped by the
// room's department theme so the right Surggies surface where you'd expect.
// Legendaries (11 Cratus, 12 Huskii, 30 Visionfox) are intentionally excluded
// from the common pools — they appear only in the broad "default" wander pool,
// where they're 1-in-many and stay appropriately rare.
const ROOM_SPECIES: Record<string, number[]> = {
  // sales line: Salespup/Pitchdog + Pitchmaster, Scalelord
  sales_floor: [1, 2, 25, 28],
  // tech line: Byte/Debuggor + Scriptlet, Algomancer, Inframonk, Cryptoquant, Synthlord
  dev_dungeon: [3, 4, 21, 22, 24, 26, 29],
  // finance line: Qubit/Stonkzilla + Ledgeling, Auditlord, Algomancer, Cryptoquant
  finance_hub: [5, 6, 19, 20, 22, 26],
  // creative line: Vibecat/Brandlion + Buzzlet, Trendlion, Growthhive, Synthlord
  creative_lab: [7, 8, 17, 18, 27, 29],
  // ops line: Opster/Sysmonk + Supportpup, Deploybot, Inframonk, Growthhive, Scalelord
  ops_center: [9, 10, 15, 23, 24, 27, 28],
  // hr line: Recruitling/Hiremaster + Helpmaid (ops/hr hybrid)
  hr_office: [13, 14, 16],
  // broad wander pool — many ids can surface here, including rare legendaries.
  default: [1, 3, 5, 7, 9, 11, 12, 13, 15, 17, 19, 21, 23, 25, 27, 30],
};

export type EncounterState =
  | "idle"        // No encounter, player exploring
  | "searching"   // Player in tall grass/sand, rolling for encounter
  | "encountered" // Wild Surggie appeared, showing intro
  | "battle"      // Battle in progress
  | "caught"      // Successful catch
  | "escaped";    // Player ran or defeated

export interface WildSurggie {
  id: string;
  speciesId: number;
  name: string;
  role: string;
  team: string;
  sprite_key: string;
  level: number;
  max_hp: number;
  current_hp: number;
  resistance: number; // catch resistance
  position: Position;
}

export interface EncounterResult {
  outcome: "caught" | "escaped" | "defeated";
  wild_surggie: WildSurggie;
  xp_earned?: number;
  recruited?: boolean;
}

type EncounterCallback = (result: EncounterResult) => void;

export const ENCOUNTER_CHANCE = 0.08; // 8% chance per step in encounter zone
const CATCH_BASE_CHANCE = 0.4;
const CATCH_RESISTANCE_FACTOR = 0.05;

export class EncounterSystem {
  private state: EncounterState = "idle";
  private wildSurggie: WildSurggie | null = null;
  private encounterCallback: EncounterCallback | null = null;
  private lastEncounterTile: Position | null = null;
  private roomId: string = "default";
  private zone: Zone | null = null;

  /**
   * Set the current room so encounters draw from the right species pool.
   */
  setRoomId(roomId: string): void {
    this.roomId = roomId;
  }

  /**
   * Set the current zone — overrides roomId and encounterChance if set.
   */
  setZone(zone: Zone): void {
    this.zone = zone;
    this.roomId = zone.roomId;
  }

  /**
   * Set callback for when encounter resolves.
   */
  onEncounterResult(cb: EncounterCallback): void {
    this.encounterCallback = cb;
  }

  /**
   * Check if player stepped on an encounter tile.
   * Returns true if a new encounter was triggered.
   */
  checkPosition(
    playerX: number,
    playerY: number,
    tileType: number,
    isInEncounterZone: boolean,
  ): boolean {
    if (this.state !== "idle" && this.state !== "searching") {
      return false;
    }

    // Must be in encounter zone (tall grass / sand dunes)
    if (!isInEncounterZone) {
      this.state = "idle";
      this.lastEncounterTile = null;
      return false;
    }

    const currentTile = { x: playerX, y: playerY };

    // Already searched this tile
    if (
      this.lastEncounterTile &&
      this.lastEncounterTile.x === currentTile.x &&
      this.lastEncounterTile.y === currentTile.y
    ) {
      return false;
    }

    this.lastEncounterTile = currentTile;

    // Roll for encounter
    if (Math.random() < (this.zone?.encounterChance ?? ENCOUNTER_CHANCE)) {
      return this.triggerEncounter();
    }

    this.state = "searching";
    return false;
  }

  /**
   * Trigger a new wild Surggie encounter (random from current room/zone pool).
   */
  triggerEncounter(): boolean {
    this.state = "encountered";
    this.wildSurggie = this.generateWildSurggie();
    return true;
  }

  /**
   * Trigger a new wild Surggie encounter using an explicit speciesId + level.
   * Used by the visible wild-sprite proximity trigger path (Phase 3.1).
   * Returns the freshly built WildSurggie, or null if the speciesId is unknown.
   */
  triggerEncounterWith(speciesId: number, level: number): WildSurggie | null {
    const wild = this.generateWildSurggieFor(speciesId, level);
    if (!wild) return null;
    this.state = "encountered";
    this.wildSurggie = wild;
    return wild;
  }

  /**
   * Build a WildSurggie from an explicit speciesId + level, bypassing the
   * zone/room random pool. Public so callers (EncounterManager) can drive
   * encounters from a visible wild sprite the player walked into.
   * Returns null if the speciesId is not in SPECIES_DEX.
   */
  generateWildSurggieFor(speciesId: number, level: number): WildSurggie | null {
    const species = SPECIES_DEX.find((s) => s.id === speciesId);
    if (!species) return null;
    const clampedLevel = Math.max(1, Math.floor(level));
    const maxHp = 30 + clampedLevel * 5;
    return {
      id: `wild_${Date.now()}_${species.id}`,
      speciesId: species.id,
      name: species.name,
      role: species.type1,
      team: species.type1,
      sprite_key: species.sprite,
      level: clampedLevel,
      max_hp: maxHp,
      current_hp: maxHp,
      resistance: 2 + Math.floor(clampedLevel / 2),
      position: { x: 0, y: 0 },
    };
  }

  /**
   * Start the battle phase.
   */
  startBattle(): void {
    if (this.state !== "encountered") return;
    this.state = "battle";
  }

  /**
   * Attempt to catch the wild Surggie.
   * Returns true if caught.
   */
  attemptCatch(hasRecruitmentToken: boolean): boolean {
    if (!this.wildSurggie || this.state !== "battle") return false;

    if (!hasRecruitmentToken) {
      // No token — auto escape
      this.state = "escaped";
      return false;
    }

    // Calculate catch probability
    const hpRatio = this.wildSurggie.current_hp / this.wildSurggie.max_hp;
    const resistanceFactor = this.wildSurggie.resistance * CATCH_RESISTANCE_FACTOR;
    const catchChance = CATCH_BASE_CHANCE * hpRatio - resistanceFactor;
    const caught = Math.random() < Math.max(0.05, catchChance);

    if (caught) {
      this.state = "caught";
    } else {
      // Failed catch — resistance drops
      this.wildSurggie.resistance = Math.max(0, this.wildSurggie.resistance - 1);
    }

    return caught;
  }

  /**
   * Attempt to run from battle.
   */
  attemptRun(): boolean {
    if (this.state !== "battle" && this.state !== "encountered") return false;
    this.state = "escaped";
    return true;
  }

  /**
   * Defeat the wild Surggie (player wins battle).
   */
  defeatWild(): void {
    if (!this.wildSurggie) return;
    this.state = "escaped"; // treated as "defeated" in result
  }

  /**
   * Resolve the encounter and get result.
   */
  resolve(): EncounterResult | null {
    if (this.state === "idle" || this.state === "searching" || this.state === "battle") {
      return null;
    }

    if (!this.wildSurggie) return null;

    const outcome =
      this.state === "caught"
        ? "caught"
        : this.state === "escaped"
          ? "escaped"
          : "defeated";

    const result: EncounterResult = {
      outcome,
      wild_surggie: this.wildSurggie,
    };

    if (outcome === "caught") {
      result.xp_earned = Math.floor(20 + this.wildSurggie.level * 5);
      result.recruited = true;
    }

    // Reset state
    this.state = "idle";
    const oldSurggie = this.wildSurggie;
    this.wildSurggie = null;

    // Fire callback
    if (this.encounterCallback) {
      this.encounterCallback(result);
    }

    return result;
  }

  /**
   * Get current encounter state.
   */
  getState(): EncounterState {
    return this.state;
  }

  /**
   * Get the wild Surggie if in encounter.
   */
  getWildSurggie(): WildSurggie | null {
    return this.wildSurggie;
  }

  /**
   * Generate a random wild Surggie from the species dex, using the current room's pool.
   */
  private generateWildSurggie(): WildSurggie {
    const pool = ROOM_SPECIES[this.roomId] ?? ROOM_SPECIES.default;
    const speciesId = pool[Math.floor(Math.random() * pool.length)];
    const species = SPECIES_DEX.find((s) => s.id === speciesId) ?? SPECIES_DEX[0];
    const level = Math.floor(3 + Math.random() * 8); // 3–10

    const maxHp = 30 + level * 5;

    return {
      id: `wild_${Date.now()}`,
      speciesId: species.id,
      name: species.name,
      role: species.type1,
      team: species.type1,
      sprite_key: species.sprite,
      level,
      max_hp: maxHp,
      current_hp: maxHp,
      resistance: 2 + Math.floor(level / 2),
      position: { x: 0, y: 0 },
    };
  }

  /**
   * Reset encounter state.
   */
  reset(): void {
    this.state = "idle";
    this.wildSurggie = null;
    this.lastEncounterTile = null;
  }
}

export const globalEncounterSystem = new EncounterSystem();
